import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PayWidget from "../../../../components/PayWidget";
import { PostPayment } from "../../../../services/PaymentService";
import { registerAthlete } from "../../../../services/AthleteService";
import {
  registerAccountStore,
  setSubscriptionDetails,
} from "../../../../store/slices/accountSlice";
import { GetAdminPrice } from "../../../../services/EventsService";
import { useAxiosInstance } from "../../../../services/Interceptor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

function AthleteFreeSubscription() {
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [adminPricing, setAdminPricing] = useState({
    monthlyPrice: 0,
    annualPrice: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showPayWidget, setShowPayWidget] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        const adminPrices = await GetAdminPrice(apiInstance);

        const athletePricing = adminPrices.data.find(
          (option) => option.role.toLowerCase() === "athlete",
        );

        if (athletePricing?.pricingOptions) {
          setAdminPricing({
            monthlyPrice: athletePricing.pricingOptions.monthlyPrice ?? 0,
            annualPrice: athletePricing.pricingOptions.annualPrice ?? 0,
          });
          setShowPayWidget(true);
        }
      } catch (err) {
        console.error("Error fetching admin prices:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [apiInstance]);

  const selectedPrice =
    subscriptionType === "monthly"
      ? adminPricing.monthlyPrice
      : adminPricing.annualPrice;

  useEffect(() => {
    dispatch(
      setSubscriptionDetails({
        subscriptionType,
        subscriptionPrice: selectedPrice,
      }),
    );
  }, [dispatch, subscriptionType, selectedPrice]);

  const handlePayClick = async () => {
    try {
      const reg = await registerAthlete(apiInstance, "0", user);

      if (reg.status === 200) {
        const newUserId = reg.data.id;

        const updatedPaymentPayload = {
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: subscriptionType === "annual",
          isMonthlySubscription: subscriptionType === "monthly",
          isCash: false,
          eventId: user.eventId ?? 0,
          transactionId: "N/A",
          intellipayToken: "",
        };

        const paid = await PostPayment(apiInstance, updatedPaymentPayload);
        if (paid.status === 200) {
          toast.success("Athlete registration is successful!", {
            position: "top-right",
          });
          setTimeout(() => {
            navigate("/register/registrationsuccess");
          }, 3000);
        } else {
          toast.error("Payment after athlete registration failed.", {
            position: "top-right",
          });
        }
      } else {
        toast.error("Athlete registration failed.", {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Something went wrong during registration.", {
        position: "top-right",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex items-center justify-center mt-6">
      <div className="w-full max-w-4xl px-3">
        <div className="text-center text-white uppercase text-2xl xl:text-3xl lg:text-3xl font-black italic">
          Simple, Transparent Pricing
        </div>
        <p className="text-center text-white text-base font-normal py-2">
          Clear, straightforward pricing with no hidden fees.
        </p>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-4xl text-white"
            />{" "}
          </div>
        ) : (
          <>
            <div className="mt-0 grid justify-items-center">
              <div className="price-top flex gap-4">
                <button
                  onClick={() => setSubscriptionType("monthly")}
                  className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "monthly" ? "bg-red-500" : "bg-gray-600"}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSubscriptionType("annual")}
                  className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "annual" ? "bg-red-500" : "bg-gray-600"}`}
                >
                  Annual
                </button>
              </div>

              <div className="w-full mt-6">
                <div className="rounded-2xl border border-white-600">
                  <div className="flex flex-col px-6 py-4 text-white space-y-3">
                    <div className="flex justify-between">
                      <span>
                        {subscriptionType === "monthly"
                          ? "Monthly Subscription"
                          : "Annual Subscription"}
                      </span>
                      <span>${selectedPrice.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-white-600 my-2"></div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>${selectedPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid justify-items-center">
              {selectedPrice === 0 ? (
                <button
                  type="button"
                  onClick={handlePayClick}
                  disabled={isProcessing}
                  className="bg-red-500 mt-8 text-white uppercase rounded-full px-12 py-3 hover:bg-red-700 cursor-pointer"
                >
                  {isProcessing ? "Processing..." : "Register Free"}
                </button>
              ) : (
                <button
                  type="button"
                  className="bg-red-500 mt-8 text-white uppercase rounded-full px-12 py-3 hover:bg-red-700 cursor-pointer ipayfield"
                  data-ipayname="submit"
                >
                  Pay
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {showPayWidget && selectedPrice > 0 && (
        <PayWidget
          userEmail={user?.email}
          userId={user?.id}
          paymentAmount={selectedPrice}
          eventId={user?.eventId}
          subscriptionType={subscriptionType}
        />
      )}
    </div>
  );
}

export default AthleteFreeSubscription;
